import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  CardHeader,
  InputAdornment,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  Save,
  Cancel,
  Business,
  Store,
  Person,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { partnerService } from '../services/api';
import { countries } from '../data/countries';
import { formatPhoneNumber, getPhoneNumberError } from '../utils/phoneValidation';
import { getUrlError } from '../utils/urlValidation';
import { getErrorMessage } from '../utils/errorUtils';
import NotificationSnackbar, { NotificationType } from '../components/NotificationSnackbar';

type RetailerType = 'POST_PAID' | 'PRE_PAID';

interface Partner {
  id: number;
  partnerCode: string;
  partnerName: string;
  partnerType: 'MERCHANT' | 'RETAILER' | 'AGENT';
  retailerType?: RetailerType;
  partnerStatus: string;

  // Contact
  email: string;
  phoneNumber: string;
  alternativePhone?: string;

  // Business
  businessRegistrationNumber?: string;
  taxIdentificationNumber?: string;

  // Address
  businessAddress: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  country?: string;

  // Primary contact
  primaryContactName?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string;

  // Financial
  defaultCommissionRate: number;
  specialCommissionRate?: number;
  commissionCap?: number;
  settlementFrequency: string;
  settlementDelayDays?: number;
  minimumSettlementAmount?: number;
  transactionLimitDaily?: number;
  transactionLimitMonthly?: number;
  riskLevel?: string;

  // Callback configuration (merchants only)
  callbackUrl?: string;
}

const EditPartner: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [partner, setPartner] = useState<Partner | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [callbackUrlError, setCallbackUrlError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    type: NotificationType;
  }>({ open: false, message: '', type: 'success' });

  const showNotification = (message: string, type: NotificationType = 'success') => {
    setNotification({ open: true, message, type });
  };

  const [formData, setFormData] = useState({
    partnerName: '',
    email: '',
    phoneNumber: '',
    alternativePhone: '',
    businessRegistrationNumber: '',
    taxIdentificationNumber: '',
    businessAddress: '',
    city: '',
    stateProvince: '',
    postalCode: '',
    country: 'ZW',
    primaryContactName: '',
    primaryContactEmail: '',
    primaryContactPhone: '',
    defaultCommissionRate: 0,
    specialCommissionRate: undefined as number | undefined,
    commissionCap: undefined as number | undefined,
    customCommission: false,
    settlementFrequency: 'T+1',
    settlementDelayDays: 1,
    minimumSettlementAmount: undefined as number | undefined,
    transactionLimitDaily: 10000,
    transactionLimitMonthly: 100000,
    riskLevel: 'LOW',
    retailerType: 'POST_PAID' as RetailerType,
    callbackUrl: '',
  });

  useEffect(() => {
    fetchPartner();
  }, [id]);

  const fetchPartner = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const data = await partnerService.getPartner(id);
      setPartner(data);

      setFormData({
        partnerName: data.partnerName,
        email: data.email,
        phoneNumber: data.phoneNumber,
        alternativePhone: data.alternativePhone || '',
        businessRegistrationNumber: data.businessRegistrationNumber || '',
        taxIdentificationNumber: data.taxIdentificationNumber || '',
        businessAddress: data.businessAddress,
        city: data.city || '',
        stateProvince: data.stateProvince || '',
        postalCode: data.postalCode || '',
        country: data.country || 'ZW',
        primaryContactName: data.primaryContactName || '',
        primaryContactEmail: data.primaryContactEmail || '',
        primaryContactPhone: data.primaryContactPhone || '',
        defaultCommissionRate: data.defaultCommissionRate,
        specialCommissionRate: data.specialCommissionRate,
        commissionCap: data.commissionCap,
        customCommission: data.specialCommissionRate !== undefined && data.specialCommissionRate !== null,
        settlementFrequency: data.settlementFrequency,
        settlementDelayDays: data.settlementDelayDays || 1,
        minimumSettlementAmount: data.minimumSettlementAmount,
        transactionLimitDaily: data.transactionLimitDaily || 10000,
        transactionLimitMonthly: data.transactionLimitMonthly || 100000,
        riskLevel: data.riskLevel || 'LOW',
        retailerType: data.retailerType || 'POST_PAID',
        callbackUrl: data.callbackUrl || '',
      });
    } catch (error) {
      console.error('Failed to fetch partner:', error);
      const errorMessage = getErrorMessage(error, 'Failed to load partner details. Please try again.');
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    setFormData({ ...formData, phoneNumber: formatted });

    const error = getPhoneNumberError(formatted);
    setPhoneError(error);
  };

  const handleCallbackUrlChange = (value: string) => {
    setFormData({ ...formData, callbackUrl: value });

    // Only validate if there's a value (since it's optional)
    if (value && value.trim() !== '') {
      const error = getUrlError(value, 'Callback URL', false);
      setCallbackUrlError(error);
    } else {
      setCallbackUrlError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!id || !partner) return;

    // Validate phone before saving
    const phoneValidationError = getPhoneNumberError(formData.phoneNumber);
    if (phoneValidationError) {
      setPhoneError(phoneValidationError);
      return;
    }

    // Validate callback URL if provided for merchants
    if (partner.partnerType === 'MERCHANT' && formData.callbackUrl && formData.callbackUrl.trim() !== '') {
      const urlValidationError = getUrlError(formData.callbackUrl, 'Callback URL', false);
      if (urlValidationError) {
        setCallbackUrlError(urlValidationError);
        setError(urlValidationError);
        return;
      }
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const updateData: any = {
        partnerName: formData.partnerName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        alternativePhone: formData.alternativePhone || undefined,
        businessRegistrationNumber: formData.businessRegistrationNumber || undefined,
        taxIdentificationNumber: formData.taxIdentificationNumber || undefined,
        businessAddress: formData.businessAddress,
        city: formData.city,
        stateProvince: formData.stateProvince || undefined,
        postalCode: formData.postalCode || undefined,
        country: formData.country,
        primaryContactName: formData.primaryContactName,
        primaryContactEmail: formData.primaryContactEmail || undefined,
        primaryContactPhone: formData.primaryContactPhone || undefined,
        defaultCommissionRate: formData.defaultCommissionRate,
        specialCommissionRate: formData.customCommission ? formData.specialCommissionRate : undefined,
        commissionCap: formData.commissionCap,
        settlementFrequency: formData.settlementFrequency,
        settlementDelayDays: formData.settlementDelayDays,
        minimumSettlementAmount: formData.minimumSettlementAmount,
        transactionLimitDaily: formData.transactionLimitDaily,
        transactionLimitMonthly: formData.transactionLimitMonthly,
        riskLevel: formData.riskLevel,
      };

      // Include retailerType if it's a retailer
      if (partner.partnerType === 'RETAILER') {
        updateData.retailerType = formData.retailerType;
      }

      // Include callbackUrl if it's a merchant
      if (partner.partnerType === 'MERCHANT') {
        updateData.callbackUrl = formData.callbackUrl || undefined;
      }

      console.log('Updating partner with data:', updateData);

      await partnerService.updatePartner(id, updateData);
      setSuccess('Partner updated successfully!');

      // Redirect back to partner details after 1.5 seconds
      setTimeout(() => {
        navigate(`/partners/${id}`);
      }, 1500);
    } catch (error: any) {
      console.error('Failed to update partner:', error);
      const errorMessage = getErrorMessage(error, 'Failed to update partner. Please try again.');
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const getPartnerIcon = (type: string) => {
    switch (type) {
      case 'MERCHANT': return <Business />;
      case 'RETAILER': return <Store />;
      case 'AGENT': return <Person />;
      default: return <Business />;
    }
  };

  const formatRetailerType = (type: RetailerType): string => {
    return type === 'POST_PAID' ? 'Post-paid' : 'Pre-paid';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!partner) {
    return (
      <Box>
        <Alert severity="error">Partner not found.</Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/partners')}
          sx={{ mt: 2 }}
        >
          Back to Partners
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(`/partners/${id}`)}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4">Edit Partner</Typography>
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              {getPartnerIcon(partner.partnerType)}
              <Typography variant="body2" color="textSecondary">
                {partner.partnerCode} - {partner.partnerType}
                {partner.partnerType === 'RETAILER' && partner.retailerType && (
                  <> ({formatRetailerType(partner.retailerType)})</>
                )}
              </Typography>
              <Chip
                label={partner.partnerStatus}
                color={partner.partnerStatus === 'ACTIVE' ? 'success' : 'default'}
                size="small"
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Basic Information" />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Partner Name"
                      value={formData.partnerName}
                      onChange={(e) => setFormData({ ...formData, partnerName: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Partner Code"
                      value={partner.partnerCode}
                      disabled
                      helperText="Partner code cannot be changed"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      value={formData.phoneNumber}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      error={!!phoneError}
                      helperText={phoneError || 'Format: +263712345678 or 712345678'}
                      placeholder="+263712345678"
                      required
                      inputProps={{
                        maxLength: 16,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Alternative Phone"
                      value={formData.alternativePhone}
                      onChange={(e) => setFormData({ ...formData, alternativePhone: e.target.value })}
                      placeholder="+263712345678"
                      inputProps={{
                        maxLength: 16,
                      }}
                    />
                  </Grid>
                  {partner.partnerType === 'RETAILER' && (
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth required>
                        <InputLabel>Retailer Type</InputLabel>
                        <Select
                          value={formData.retailerType}
                          label="Retailer Type"
                          onChange={(e) => setFormData({ ...formData, retailerType: e.target.value as RetailerType })}
                        >
                          <MenuItem value="POST_PAID">Post-paid Retailer</MenuItem>
                          <MenuItem value="PRE_PAID">Pre-paid Agent</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Address Information */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Address Information" />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Business Address"
                      value={formData.businessAddress}
                      onChange={(e) => setFormData({ ...formData, businessAddress: e.target.value })}
                      multiline
                      rows={2}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="City"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="State/Province"
                      value={formData.stateProvince}
                      onChange={(e) => setFormData({ ...formData, stateProvince: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Postal Code"
                      value={formData.postalCode}
                      onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <Autocomplete
                      fullWidth
                      options={countries}
                      getOptionLabel={(option) => option.name}
                      value={countries.find(c => c.code === formData.country) || null}
                      onChange={(e, newValue) => setFormData({ ...formData, country: newValue?.code || '' })}
                      renderOption={(props, option) => (
                        <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                          <Box component="span" sx={{ mr: 1, fontWeight: 'bold' }}>
                            {option.code}
                          </Box>
                          {option.name}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Country"
                          inputProps={{
                            ...params.inputProps,
                            autoComplete: 'new-password',
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Business Details */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Business Details" />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Business Registration Number"
                      value={formData.businessRegistrationNumber}
                      onChange={(e) => setFormData({ ...formData, businessRegistrationNumber: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Tax Identification Number"
                      value={formData.taxIdentificationNumber}
                      onChange={(e) => setFormData({ ...formData, taxIdentificationNumber: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Primary Contact */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Primary Contact" />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Contact Name"
                      value={formData.primaryContactName}
                      onChange={(e) => setFormData({ ...formData, primaryContactName: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Contact Email"
                      type="email"
                      value={formData.primaryContactEmail}
                      onChange={(e) => setFormData({ ...formData, primaryContactEmail: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Contact Phone"
                      value={formData.primaryContactPhone}
                      onChange={(e) => setFormData({ ...formData, primaryContactPhone: e.target.value })}
                      placeholder="+263712345678"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Financial Settings */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Financial Settings" />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Settlement Frequency</InputLabel>
                      <Select
                        value={formData.settlementFrequency}
                        label="Settlement Frequency"
                        onChange={(e) => setFormData({ ...formData, settlementFrequency: e.target.value })}
                      >
                        <MenuItem value="INSTANT">Instant</MenuItem>
                        <MenuItem value="T+1">T+1 (Next day)</MenuItem>
                        <MenuItem value="T+7">T+7 (Weekly)</MenuItem>
                        <MenuItem value="T+14">T+14 (Bi-weekly)</MenuItem>
                        <MenuItem value="T+30">T+30 (Monthly)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Settlement Delay (Days)"
                      type="number"
                      value={formData.settlementDelayDays}
                      onChange={(e) => setFormData({ ...formData, settlementDelayDays: parseInt(e.target.value) })}
                      inputProps={{ min: 0, max: 30 }}
                      helperText="Number of days to delay settlements"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Default Commission Rate"
                      type="number"
                      value={formData.defaultCommissionRate}
                      onChange={(e) => setFormData({ ...formData, defaultCommissionRate: parseFloat(e.target.value) })}
                      disabled={!formData.customCommission}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                      inputProps={{ min: 0, max: 100, step: 0.1 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.customCommission}
                          onChange={(e) => setFormData({ ...formData, customCommission: e.target.checked })}
                        />
                      }
                      label="Enable Custom Commission Rate"
                    />
                  </Grid>
                  {formData.customCommission && (
                    <>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Special Commission Rate"
                          type="number"
                          value={formData.specialCommissionRate || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            specialCommissionRate: e.target.value ? parseFloat(e.target.value) : undefined
                          })}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                          inputProps={{ min: 0, max: 100, step: 0.1 }}
                          helperText="Override default commission rate"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Commission Cap"
                          type="number"
                          value={formData.commissionCap || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            commissionCap: e.target.value ? parseFloat(e.target.value) : undefined
                          })}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                          inputProps={{ min: 0, step: 0.01 }}
                          helperText="Maximum commission amount per transaction"
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Minimum Settlement Amount"
                      type="number"
                      value={formData.minimumSettlementAmount || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        minimumSettlementAmount: e.target.value ? parseFloat(e.target.value) : undefined
                      })}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                      helperText="Minimum amount before settlement is triggered"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Transaction Limits */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Transaction Limits & Risk Settings" />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Daily Transaction Limit"
                      type="number"
                      value={formData.transactionLimitDaily}
                      onChange={(e) => setFormData({ ...formData, transactionLimitDaily: parseFloat(e.target.value) })}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 100 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Monthly Transaction Limit"
                      type="number"
                      value={formData.transactionLimitMonthly}
                      onChange={(e) => setFormData({ ...formData, transactionLimitMonthly: parseFloat(e.target.value) })}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 1000 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Risk Level</InputLabel>
                      <Select
                        value={formData.riskLevel}
                        label="Risk Level"
                        onChange={(e) => setFormData({ ...formData, riskLevel: e.target.value })}
                      >
                        <MenuItem value="LOW">
                          <Chip label="Low" color="success" size="small" sx={{ mr: 1 }} />
                          Low Risk
                        </MenuItem>
                        <MenuItem value="MEDIUM">
                          <Chip label="Medium" color="warning" size="small" sx={{ mr: 1 }} />
                          Medium Risk
                        </MenuItem>
                        <MenuItem value="HIGH">
                          <Chip label="High" color="error" size="small" sx={{ mr: 1 }} />
                          High Risk
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Callback Configuration (Merchants Only) */}
          {partner?.partnerType === 'MERCHANT' && (
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Callback Configuration" />
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Callback URL"
                        value={formData.callbackUrl || ''}
                        onChange={(e) => handleCallbackUrlChange(e.target.value)}
                        placeholder="https://your-merchant-domain.com/webhook/voucher-payment"
                        helperText={callbackUrlError || "URL to receive payment notifications when vouchers are paid by customers. Leave empty to disable callbacks."}
                        error={!!callbackUrlError}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">🔗</InputAdornment>,
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={() => navigate(`/partners/${id}`)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                  disabled={saving || !!phoneError || !!callbackUrlError}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </form>

      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ ...notification, open: false })}
      />
    </Box>
  );
};

export default EditPartner;
